import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Search, CheckCircle, X, Users, UserPlus, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import {api} from '../../api';

interface Student {
  _id: string;
  name: string;
  email: string;
  avatar?: string;
  isEnrolled?: boolean;
}

const CourseAssignment: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState<any>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<Student[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudents, setSelectedStudents] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    loadCourse();
    loadStudents();
  }, [id]);

  useEffect(() => {
    filterStudents();
  }, [searchTerm, students]);

  const loadCourse = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/professor/courses/${id}`);
      if (response.data && response.data.data) {
        setCourse(response.data.data);
        setSelectedStudents(response.data.data.students?.map((s: any) => s._id) || []);
      } else {
        toast.error('Erreur lors du chargement du cours');
        navigate('/professor/courses');
      }
    } catch (error) {
      console.error('Load course error:', error);
      toast.error('Erreur lors du chargement du cours');
      navigate('/professor/courses');
    } finally {
      setLoading(false);
    }
  };

  const loadStudents = async () => {
    try {
      setLoading(true);
      const response = await api.get('/professor/users?role=student');
      if (response.data && response.data.users) {
        setStudents(response.data.users);
      } else {
        toast.error('Erreur lors du chargement des étudiants');
      }
    } catch (error) {
      console.error('Load students error:', error);
      toast.error('Erreur lors du chargement des étudiants');
    } finally {
      setLoading(false);
    }
  };

  const filterStudents = () => {
    if (!searchTerm.trim()) {
      setFilteredStudents(students);
      return;
    }

    const filtered = students.filter(student => 
      student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.email.toLowerCase().includes(searchTerm.toLowerCase())
    );
    
    setFilteredStudents(filtered);
  };

  const toggleStudentSelection = (studentId: string) => {
    if (selectedStudents.includes(studentId)) {
      setSelectedStudents(selectedStudents.filter(id => id !== studentId));
    } else {
      setSelectedStudents([...selectedStudents, studentId]);
    }
  };

  const selectAllStudents = () => {
    const nonEnrolledStudents = filteredStudents
      .filter(student => !student.isEnrolled)
      .map(student => student._id);
    
    setSelectedStudents(nonEnrolledStudents);
  };

  const clearSelection = () => {
    setSelectedStudents([]);
  };

  const assignCourse = async () => {
    if (selectedStudents.length === 0) {
      toast.error('Veuillez sélectionner au moins un étudiant');
      return;
    }

    try {
      setAssigning(true);
      
      const response = await api.post(`/professor/courses/${id}/assign`, {
        studentIds: selectedStudents
      });

      if (response.data) {
        const result = response.data;
        toast.success(`Cours assigné à ${result.assignedStudents.length} étudiant(s)`);
        
        setStudents(students.map(student => ({
          ...student,
          isEnrolled: student.isEnrolled || selectedStudents.includes(student._id)
        })));
        
        setSelectedStudents([]);
      }
    } catch (error: any) {
      console.error('Assignment error:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de l\'assignation du cours');
    } finally {
      setAssigning(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-brand-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Assigner le cours</h1>
            <p className="text-gray-600">{course?.title}</p>
          </div>
          <button
            onClick={() => navigate(`/professor/courses`)}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Retour aux cours
          </button>
        </div>

        {/* Stats */}
        <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-brand-100 rounded-lg">
              <Users className="h-6 w-6 text-brand-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Étudiants inscrits</p>
              <p className="text-2xl font-bold text-gray-900">
                {students.filter(s => s.isEnrolled).length}
              </p>
            </div>
          </div>
        </div>

        {/* Search and Actions */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par nom ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              />
            </div>
            
            <div className="flex space-x-2">
              <button
                onClick={selectAllStudents}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Tout sélectionner
              </button>
              <button
                onClick={clearSelection}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Effacer
              </button>
            </div>
          </div>

          {selectedStudents.length > 0 && (
            <div className="flex justify-between items-center p-4 bg-brand-50 border border-brand-200 rounded-lg mb-6">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-brand-600 mr-2" />
                <span className="text-brand-800">
                  {selectedStudents.length} étudiant(s) sélectionné(s)
                </span>
              </div>
              <button
                onClick={assignCourse}
                disabled={assigning}
                className="flex items-center px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                {assigning ? 'Assignation...' : 'Assigner le cours'}
              </button>
            </div>
          )}

          {/* Students List */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <span className="sr-only">Sélection</span>
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Étudiant
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredStudents.map((student) => (
                  <tr key={student._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={selectedStudents.includes(student._id)}
                        onChange={() => toggleStudentSelection(student._id)}
                        disabled={student.isEnrolled}
                        className="rounded border-gray-300 text-brand-600 shadow-sm focus:border-brand-300 focus:ring focus:ring-brand-200 focus:ring-opacity-50 disabled:opacity-50"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-brand-600 rounded-full flex items-center justify-center text-white font-medium">
                          {student.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{student.name}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500">{student.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {student.isEnrolled ? (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-brand-100 text-brand-800">
                          Déjà inscrit
                        </span>
                      ) : (
                        <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                          Non inscrit
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredStudents.length === 0 && (
            <div className="text-center py-12">
              <AlertCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun étudiant trouvé</h3>
              <p className="text-gray-500">
                {searchTerm ? `Aucun résultat pour "${searchTerm}"` : 'Aucun étudiant disponible'}
              </p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default CourseAssignment;